/**
 * KeyRatiosTable Component
 *
 * Displays financial indicators in a multi-period table format.
 * Groups indicators by category (Solvencia, Calidad de Activos, etc.)
 * Period mode (annual/quarterly/monthly) is controlled via props.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchIndicatorsWithMode,
  exportIndicatorsMonthly,
  formatIndicatorValue,
  formatPeriodHeader,
  type IndicatorsData,
  type IndicatorRow,
} from '../../../services/analystBenchService';
import type { PeriodMode } from '../../../types/periodMode';
import { PERIOD_MODE_CONFIG } from '../../../types/periodMode';
import PeriodModeSelector from '../PeriodModeSelector';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import './AnalystBenchTable.css';

interface KeyRatiosTableProps {
  institutionId: string;
  periodMode: PeriodMode;
  onPeriodModeChange: (mode: PeriodMode) => void;
}

export default function KeyRatiosTable({
  institutionId,
  periodMode,
  onPeriodModeChange,
}: KeyRatiosTableProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<IndicatorsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function loadData() {
      if (!institutionId) return;

      setLoading(true);
      setError(null);

      try {
        const result = await fetchIndicatorsWithMode(institutionId, periodMode);
        setData(result);
        setActiveCategory((prev) =>
          prev && result.categories.some((c) => c.name === prev)
            ? prev
            : result.categories[0]?.name ?? null
        );
      } catch (err) {
        console.error('Error loading indicators:', err);
        setError(err instanceof Error ? err.message : 'Error loading data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [institutionId, periodMode]);

  const renderIndicatorRow = (indicator: IndicatorRow, periods: string[]) => (
    <tr key={indicator.id} className="abt-row">
      <td className="abt-sticky-col abt-indicator-cell">
        <div className="abt-indicator-inner">
          <span className="abt-indicator-id">{indicator.id}</span>
          <span className="abt-indicator-name">{indicator.name}</span>
        </div>
      </td>
      {periods.map((period) => (
        <td key={period} className="abt-value-cell">
          {formatIndicatorValue(indicator.values[period], indicator.id)}
        </td>
      ))}
    </tr>
  );

  const renderTable = (indicators: IndicatorRow[], periods: string[]) => {
    if (!indicators || indicators.length === 0) {
      return <EmptyState message={t('ratios.noData')} />;
    }

    return (
      <div className="abt-table-container">
        <table className="abt-table">
          <thead>
            <tr>
              <th className="abt-sticky-col abt-header-cell">{t('ratios.indicator')}</th>
              {periods.map((period) => (
                <th key={period} className="abt-header-cell abt-period-cell">
                  {formatPeriodHeader(period)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {indicators.map((indicator) => renderIndicatorRow(indicator, periods))}
          </tbody>
        </table>
      </div>
    );
  };

  const modeConfig = PERIOD_MODE_CONFIG[periodMode];

  const toSentenceCase = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const handleExport = async () => {
    if (!data) return;
    setExporting(true);
    try {
      await exportIndicatorsMonthly(institutionId, data.institutionName);
    } finally {
      setExporting(false);
    }
  };

  const pageHeader = (disabled = false) => (
    <div className="abt-page-header">
      <div className="abt-page-header-top">
        <h2 className="abt-view-title">{t('ratios.title')}</h2>
        <div className="abt-mode-info">
          <span className="abt-mode-description">{t(modeConfig.description)}</span>
          <PeriodModeSelector mode={periodMode} onChange={onPeriodModeChange} disabled={disabled} />
          <button
            className="abt-export-btn"
            onClick={handleExport}
            disabled={disabled || exporting || !data}
            title="Export monthly data as CSV"
          >
            {exporting ? (
              <span className="abt-export-spinner" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            )}
          </button>
        </div>
      </div>
      {data && data.categories.length > 0 && (
        <div className="abt-underline-tabs">
          {data.categories.map((cat) => (
            <button
              key={cat.name}
              className={`abt-underline-tab ${activeCategory === cat.name ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.name)}
              disabled={disabled}
            >
              {toSentenceCase(cat.name)}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="key-ratios-table">
        {pageHeader(true)}
        <LoadingSpinner label={t('ratios.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="key-ratios-table">
        {pageHeader()}
        <div className="abt-error">
          <p>{error}</p>
          <button
            className="abt-retry-btn"
            onClick={() => {
              setLoading(true);
              setError(null);
              fetchIndicatorsWithMode(institutionId, periodMode)
                .then(setData)
                .catch((err) => setError(err.message))
                .finally(() => setLoading(false));
            }}
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="key-ratios-table">
        {pageHeader()}
        <EmptyState />
      </div>
    );
  }

  const activeIndicators =
    data.categories.find((c) => c.name === activeCategory)?.indicators ?? [];

  return (
    <div className="key-ratios-table">
      {pageHeader()}
      {renderTable(activeIndicators, data.periods)}
    </div>
  );
}
